"use client";

import { c as _c } from "react/compiler-runtime";
import type { CompetitionStats, GoalDistribution } from "fansunited-data-layer";
import { SectionHeader, StatsGrid, StatsHeadToHead } from "fansunited-sports-ui";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { getH2hStyles } from "@/utilities/styles/h2hStyles";
import { createGridStyles } from "@/utilities/styles/tableStyles";

// Grid styles for 6-column layout (season progress)
const seasonGridStyles = createGridStyles({
  colsDesktop: 6
});

// Grid styles for 3-column layout (stats)
const statsGridStyles = createGridStyles({
  colsDesktop: 3
});
interface StatsTabProps {
  competitionStats: CompetitionStats;
  goalDistribution: GoalDistribution;
}
export function StatsTab(t0) {
  const $ = _c(190);
  const {
    competitionStats,
    goalDistribution
  } = t0;
  const labels = useLabels().standings.stats;
  if (!goalDistribution) {
    return null;
  }
  let t1;
  if ($[0] !== labels.seasonProgress) {
    t1 = <span className="text-sm font-medium text-foreground">{labels.seasonProgress}</span>;
    $[0] = labels.seasonProgress;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = competitionStats.finishedMatches;
  const t3 = competitionStats.totalMatches;
  const t4 = labels.matches;
  let t5;
  if ($[2] !== competitionStats.completionPercentage) {
    t5 = competitionStats.completionPercentage.toFixed(1);
    $[2] = competitionStats.completionPercentage;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== labels.complete || $[5] !== t5) {
    t6 = <span className="text-xs text-muted-foreground ml-2">({t5}% {labels.complete})</span>;
    $[4] = labels.complete;
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== competitionStats.finishedMatches || $[8] !== competitionStats.totalMatches || $[9] !== labels.matches || $[10] !== t6) {
    t7 = <div className="text-right"><div><span className="text-sm text-muted-foreground">{t2} / {t3} {t4}{t6}</span></div></div>;
    $[7] = competitionStats.finishedMatches;
    $[8] = competitionStats.totalMatches;
    $[9] = labels.matches;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t1 || $[13] !== t7) {
    t8 = <div className="flex items-end justify-between mb-3">{t1}{t7}</div>;
    $[12] = t1;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const t9 = `${competitionStats.completionPercentage}%`;
  let t10;
  if ($[15] !== t9) {
    t10 = <div className="w-full bg-muted rounded-full h-3"><div className="bg-primary h-3 rounded-full transition-all duration-300" style={{
        width: t9
      }} /></div>;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== t10 || $[18] !== t8) {
    t11 = <div className="bg-card border border-border rounded-lg p-6">{t8}{t10}</div>;
    $[17] = t10;
    $[18] = t8;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== labels.matchOutcomes) {
    t12 = {
      name: labels.matchOutcomes
    };
    $[20] = labels.matchOutcomes;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      container: "mb-4 text-lg"
    };
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== t12) {
    t14 = <SectionHeader entity={t12} styles={t13} />;
    $[23] = t12;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  const t15 = labels.homeWins;
  const t16 = competitionStats.homeWins;
  let t17;
  if ($[25] !== competitionStats.homeWinPercentage) {
    t17 = competitionStats.homeWinPercentage.toFixed(1);
    $[25] = competitionStats.homeWinPercentage;
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  const t18 = `${t16} (${t17}%)`;
  let t19;
  if ($[27] !== labels.homeWins || $[28] !== t18) {
    t19 = {
      id: "homeWins",
      label: t15,
      value: t18
    };
    $[27] = labels.homeWins;
    $[28] = t18;
    $[29] = t19;
  } else {
    t19 = $[29];
  }
  const t20 = labels.draws;
  const t21 = competitionStats.draws;
  let t22;
  if ($[30] !== competitionStats.drawPercentage) {
    t22 = competitionStats.drawPercentage.toFixed(1);
    $[30] = competitionStats.drawPercentage;
    $[31] = t22;
  } else {
    t22 = $[31];
  }
  const t23 = `${t21} (${t22}%)`;
  let t24;
  if ($[32] !== labels.draws || $[33] !== t23) {
    t24 = {
      id: "draws",
      label: t20,
      value: t23
    };
    $[32] = labels.draws;
    $[33] = t23;
    $[34] = t24;
  } else {
    t24 = $[34];
  }
  const t25 = labels.awayWins;
  const t26 = competitionStats.awayWins;
  let t27;
  if ($[35] !== competitionStats.awayWinPercentage) {
    t27 = competitionStats.awayWinPercentage.toFixed(1);
    $[35] = competitionStats.awayWinPercentage;
    $[36] = t27;
  } else {
    t27 = $[36];
  }
  const t28 = `${t26} (${t27}%)`;
  let t29;
  if ($[37] !== labels.awayWins || $[38] !== t28) {
    t29 = {
      id: "awayWins",
      label: t25,
      value: t28
    };
    $[37] = labels.awayWins;
    $[38] = t28;
    $[39] = t29;
  } else {
    t29 = $[39];
  }
  let t30;
  if ($[40] !== t19 || $[41] !== t24 || $[42] !== t29) {
    t30 = <StatsGrid items={[t19, t24, t29]} styles={statsGridStyles} />;
    $[40] = t19;
    $[41] = t24;
    $[42] = t29;
    $[43] = t30;
  } else {
    t30 = $[43];
  }
  let t31;
  if ($[44] !== t14 || $[45] !== t30) {
    t31 = <div>{t14}{t30}</div>;
    $[44] = t14;
    $[45] = t30;
    $[46] = t31;
  } else {
    t31 = $[46];
  }
  let t32;
  if ($[47] !== goalDistribution.totalGoals) {
    t32 = <span className="text-4xl font-bold text-foreground">{goalDistribution.totalGoals}</span>;
    $[47] = goalDistribution.totalGoals;
    $[48] = t32;
  } else {
    t32 = $[48];
  }
  let t33;
  if ($[49] !== labels.totalGoals) {
    t33 = <span className="text-sm text-muted-foreground uppercase mt-1">{labels.totalGoals}</span>;
    $[49] = labels.totalGoals;
    $[50] = t33;
  } else {
    t33 = $[50];
  }
  let t34;
  if ($[51] !== t32 || $[52] !== t33) {
    t34 = <div className="flex flex-col items-center justify-center bg-card border border-border rounded-lg p-6">{t32}{t33}</div>;
    $[51] = t32;
    $[52] = t33;
    $[53] = t34;
  } else {
    t34 = $[53];
  }
  let t35;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = getH2hStyles(undefined, undefined);
    $[54] = t35;
  } else {
    t35 = $[54];
  }
  const t36 = t35.cssVars;
  let t37;
  if ($[55] !== labels.goalsByHalf) {
    t37 = {
      name: labels.goalsByHalf
    };
    $[55] = labels.goalsByHalf;
    $[56] = t37;
  } else {
    t37 = $[56];
  }
  let t38;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = {
      container: "p-4 pb-0 text-lg"
    };
    $[57] = t38;
  } else {
    t38 = $[57];
  }
  let t39;
  if ($[58] !== t37) {
    t39 = <SectionHeader entity={t37} styles={t38} />;
    $[58] = t37;
    $[59] = t39;
  } else {
    t39 = $[59];
  }
  const t40 = `${goalDistribution.byHalf.firstHalf.goals} (${goalDistribution.byHalf.firstHalf.percentage}%)`;
  const t41 = `${goalDistribution.byHalf.secondHalf.goals} (${goalDistribution.byHalf.secondHalf.percentage}%)`;
  let t42;
  if ($[60] !== goalDistribution.byHalf.firstHalf.goals || $[61] !== goalDistribution.byHalf.secondHalf.goals || $[62] !== labels.firstHalfVsSecondHalf || $[63] !== t40 || $[64] !== t41) {
    t42 = [{
      id: "byHalf",
      label: labels.firstHalfVsSecondHalf,
      leftValue: goalDistribution.byHalf.firstHalf.goals,
      rightValue: goalDistribution.byHalf.secondHalf.goals,
      leftDisplay: t40,
      rightDisplay: t41,
      variant: "bar"
    }];
    $[60] = goalDistribution.byHalf.firstHalf.goals;
    $[61] = goalDistribution.byHalf.secondHalf.goals;
    $[62] = labels.firstHalfVsSecondHalf;
    $[63] = t40;
    $[64] = t41;
    $[65] = t42;
  } else {
    t42 = $[65];
  }
  let t43;
  if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
    t43 = getH2hStyles(undefined, undefined);
    $[66] = t43;
  } else {
    t43 = $[66];
  }
  let t44;
  if ($[67] !== t42) {
    t44 = <StatsHeadToHead items={t42} styles={t43} />;
    $[67] = t42;
    $[68] = t44;
  } else {
    t44 = $[68];
  }
  let t45;
  if ($[69] !== t39 || $[70] !== t44) {
    t45 = <div className="md:col-span-2 border border-border rounded-lg  bg-card" style={t36}>{t39}{t44}</div>;
    $[69] = t39;
    $[70] = t44;
    $[71] = t45;
  } else {
    t45 = $[71];
  }
  let t46;
  if ($[72] !== t34 || $[73] !== t45) {
    t46 = <div className="grid grid-cols-1 md:grid-cols-3 gap-1">{t34}{t45}</div>;
    $[72] = t34;
    $[73] = t45;
    $[74] = t46;
  } else {
    t46 = $[74];
  }
  let t47;
  if ($[75] !== labels.goalsByTimeInterval) {
    t47 = {
      name: labels.goalsByTimeInterval
    };
    $[75] = labels.goalsByTimeInterval;
    $[76] = t47;
  } else {
    t47 = $[76];
  }
  let t48;
  if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
    t48 = {
      container: "mb-4 text-lg"
    };
    $[77] = t48;
  } else {
    t48 = $[77];
  }
  let t49;
  if ($[78] !== t47) {
    t49 = <SectionHeader entity={t47} styles={t48} />;
    $[78] = t47;
    $[79] = t49;
  } else {
    t49 = $[79];
  }
  let t50;
  if ($[80] !== goalDistribution.intervals || $[81] !== labels.min) {
    let t51;
    if ($[83] !== labels.min) {
      t51 = interval => ({
        id: interval.label,
        label: interval.label + " " + labels.min,
        value: `${interval.goals} (${interval.percentage}%)`
      });
      $[83] = labels.min;
      $[84] = t51;
    } else {
      t51 = $[84];
    }
    t50 = goalDistribution.intervals.map(t51);
    $[80] = goalDistribution.intervals;
    $[81] = labels.min;
    $[82] = t50;
  } else {
    t50 = $[82];
  }
  let t51;
  if ($[85] !== t50) {
    t51 = <StatsGrid items={t50} styles={seasonGridStyles} />;
    $[85] = t50;
    $[86] = t51;
  } else {
    t51 = $[86];
  }
  let t52;
  if ($[87] !== t49 || $[88] !== t51) {
    t52 = <div>{t49}{t51}</div>;
    $[87] = t49;
    $[88] = t51;
    $[89] = t52;
  } else {
    t52 = $[89];
  }
  let t53;
  if ($[90] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = getH2hStyles();
    $[90] = t53;
  } else {
    t53 = $[90];
  }
  const t54 = t53.cssVars;
  let t55;
  if ($[91] !== labels.goalsOverview) {
    t55 = {
      name: labels.goalsOverview
    };
    $[91] = labels.goalsOverview;
    $[92] = t55;
  } else {
    t55 = $[92];
  }
  let t56;
  if ($[93] === Symbol.for("react.memo_cache_sentinel")) {
    t56 = {
      container: "p-4 pb-0 text-lg"
    };
    $[93] = t56;
  } else {
    t56 = $[93];
  }
  let t57;
  if ($[94] !== t55) {
    t57 = <SectionHeader entity={t55} styles={t56} />;
    $[94] = t55;
    $[95] = t57;
  } else {
    t57 = $[95];
  }
  const t58 = labels.homeVsAwayGoalsPerMatch;
  const t59 = competitionStats.homeGoalsPerMatch;
  const t60 = competitionStats.awayGoalsPerMatch;
  let t61;
  if ($[96] !== competitionStats.homeGoalsPerMatch) {
    t61 = competitionStats.homeGoalsPerMatch.toFixed(2);
    $[96] = competitionStats.homeGoalsPerMatch;
    $[97] = t61;
  } else {
    t61 = $[97];
  }
  let t62;
  if ($[98] !== competitionStats.awayGoalsPerMatch) {
    t62 = competitionStats.awayGoalsPerMatch.toFixed(2);
    $[98] = competitionStats.awayGoalsPerMatch;
    $[99] = t62;
  } else {
    t62 = $[99];
  }
  let t63;
  if ($[100] !== competitionStats.awayGoalsPerMatch || $[101] !== competitionStats.homeGoalsPerMatch || $[102] !== labels.homeVsAwayGoalsPerMatch || $[103] !== t61 || $[104] !== t62) {
    t63 = [{
      id: "homeVsAwayGoals",
      label: t58,
      leftValue: t59,
      rightValue: t60,
      leftDisplay: t61,
      rightDisplay: t62,
      variant: "comparison"
    }];
    $[100] = competitionStats.awayGoalsPerMatch;
    $[101] = competitionStats.homeGoalsPerMatch;
    $[102] = labels.homeVsAwayGoalsPerMatch;
    $[103] = t61;
    $[104] = t62;
    $[105] = t63;
  } else {
    t63 = $[105];
  }
  let t64;
  if ($[106] === Symbol.for("react.memo_cache_sentinel")) {
    t64 = {
      ...getH2hStyles(),
      typography: {
        ...getH2hStyles().typography,
        label: "text-sm font-medium text-muted-foreground"
      }
    };
    $[106] = t64;
  } else {
    t64 = $[106];
  }
  let t65;
  if ($[107] !== t63) {
    t65 = <StatsHeadToHead items={t63} styles={t64} />;
    $[107] = t63;
    $[108] = t65;
  } else {
    t65 = $[108];
  }
  const t66 = labels.average;
  let t67;
  if ($[109] !== competitionStats.goalsPerMatch) {
    t67 = competitionStats.goalsPerMatch.toFixed(2);
    $[109] = competitionStats.goalsPerMatch;
    $[110] = t67;
  } else {
    t67 = $[110];
  }
  let t68;
  if ($[111] !== t67) {
    t68 = <strong>{t67}</strong>;
    $[111] = t67;
    $[112] = t68;
  } else {
    t68 = $[112];
  }
  let t69;
  if ($[113] !== labels.average || $[114] !== labels.goalsPerMatch || $[115] !== t68) {
    t69 = <div className="px-6 pb-4 text-center"><span className="text-sm text-muted-foreground">{t66}: {t68}{" "}{labels.goalsPerMatch}</span></div>;
    $[113] = labels.average;
    $[114] = labels.goalsPerMatch;
    $[115] = t68;
    $[116] = t69;
  } else {
    t69 = $[116];
  }
  let t70;
  if ($[117] !== t57 || $[118] !== t65 || $[119] !== t69) {
    t70 = <div className="border border-border rounded-lg bg-card" style={t54}>{t57}{t65}{t69}</div>;
    $[117] = t57;
    $[118] = t65;
    $[119] = t69;
    $[120] = t70;
  } else {
    t70 = $[120];
  }
  let t71;
  if ($[121] !== labels.overUnderGoals) {
    t71 = {
      name: labels.overUnderGoals
    };
    $[121] = labels.overUnderGoals;
    $[122] = t71;
  } else {
    t71 = $[122];
  }
  let t72;
  if ($[123] === Symbol.for("react.memo_cache_sentinel")) {
    t72 = {
      container: "mb-4 text-lg"
    };
    $[123] = t72;
  } else {
    t72 = $[123];
  }
  let t73;
  if ($[124] !== t71) {
    t73 = <SectionHeader entity={t71} styles={t72} />;
    $[124] = t71;
    $[125] = t73;
  } else {
    t73 = $[125];
  }
  const t74 = labels.over1_5;
  const t75 = competitionStats.over1_5Goals;
  let t76;
  if ($[126] !== competitionStats.over1_5Percentage) {
    t76 = competitionStats.over1_5Percentage.toFixed(1);
    $[126] = competitionStats.over1_5Percentage;
    $[127] = t76;
  } else {
    t76 = $[127];
  }
  const t77 = `${t75} (${t76}%)`;
  let t78;
  if ($[128] !== labels.over1_5 || $[129] !== t77) {
    t78 = {
      id: "over1_5",
      label: t74,
      value: t77
    };
    $[128] = labels.over1_5;
    $[129] = t77;
    $[130] = t78;
  } else {
    t78 = $[130];
  }
  const t79 = labels.over2_5;
  const t80 = competitionStats.over2_5Goals;
  let t81;
  if ($[131] !== competitionStats.over2_5Percentage) {
    t81 = competitionStats.over2_5Percentage.toFixed(1);
    $[131] = competitionStats.over2_5Percentage;
    $[132] = t81;
  } else {
    t81 = $[132];
  }
  const t82 = `${t80} (${t81}%)`;
  let t83;
  if ($[133] !== labels.over2_5 || $[134] !== t82) {
    t83 = {
      id: "over2_5",
      label: t79,
      value: t82
    };
    $[133] = labels.over2_5;
    $[134] = t82;
    $[135] = t83;
  } else {
    t83 = $[135];
  }
  const t84 = labels.over3_5;
  const t85 = competitionStats.over3_5Goals;
  let t86;
  if ($[136] !== competitionStats.over3_5Percentage) {
    t86 = competitionStats.over3_5Percentage.toFixed(1);
    $[136] = competitionStats.over3_5Percentage;
    $[137] = t86;
  } else {
    t86 = $[137];
  }
  const t87 = `${t85} (${t86}%)`;
  let t88;
  if ($[138] !== labels.over3_5 || $[139] !== t87) {
    t88 = {
      id: "over3_5",
      label: t84,
      value: t87
    };
    $[138] = labels.over3_5;
    $[139] = t87;
    $[140] = t88;
  } else {
    t88 = $[140];
  }
  let t89;
  if ($[141] !== t78 || $[142] !== t83 || $[143] !== t88) {
    t89 = <StatsGrid items={[t78, t83, t88]} styles={statsGridStyles} />;
    $[141] = t78;
    $[142] = t83;
    $[143] = t88;
    $[144] = t89;
  } else {
    t89 = $[144];
  }
  let t90;
  if ($[145] !== t73 || $[146] !== t89) {
    t90 = <div>{t73}{t89}</div>;
    $[145] = t73;
    $[146] = t89;
    $[147] = t90;
  } else {
    t90 = $[147];
  }
  let t91;
  if ($[148] === Symbol.for("react.memo_cache_sentinel")) {
    t91 = getH2hStyles();
    $[148] = t91;
  } else {
    t91 = $[148];
  }
  const t92 = t91.cssVars;
  let t93;
  if ($[149] !== labels.firstGoal) {
    t93 = {
      name: labels.firstGoal
    };
    $[149] = labels.firstGoal;
    $[150] = t93;
  } else {
    t93 = $[150];
  }
  let t94;
  if ($[151] === Symbol.for("react.memo_cache_sentinel")) {
    t94 = {
      container: "p-4 pb-0 text-lg"
    };
    $[151] = t94;
  } else {
    t94 = $[151];
  }
  let t95;
  if ($[152] !== t93) {
    t95 = <SectionHeader entity={t93} styles={t94} />;
    $[152] = t93;
    $[153] = t95;
  } else {
    t95 = $[153];
  }
  const t96 = labels.homeVsAway;
  const t97 = competitionStats.homeTeamScoredFirst;
  const t98 = competitionStats.awayTeamScoredFirst;
  const t99 = competitionStats.homeTeamScoredFirst;
  let t100;
  if ($[154] !== competitionStats.homeTeamScoredFirstPercentage) {
    t100 = competitionStats.homeTeamScoredFirstPercentage.toFixed(0);
    $[154] = competitionStats.homeTeamScoredFirstPercentage;
    $[155] = t100;
  } else {
    t100 = $[155];
  }
  const t101 = `${t99} (${t100}%)`;
  const t102 = competitionStats.awayTeamScoredFirst;
  let t103;
  if ($[156] !== competitionStats.awayTeamScoredFirstPercentage) {
    t103 = competitionStats.awayTeamScoredFirstPercentage.toFixed(0);
    $[156] = competitionStats.awayTeamScoredFirstPercentage;
    $[157] = t103;
  } else {
    t103 = $[157];
  }
  const t104 = `${t102} (${t103}%)`;
  let t105;
  if ($[158] !== competitionStats.awayTeamScoredFirst || $[159] !== competitionStats.homeTeamScoredFirst || $[160] !== labels.homeVsAway || $[161] !== t101 || $[162] !== t104) {
    t105 = [{
      id: "firstGoal",
      label: t96,
      leftValue: t97,
      rightValue: t98,
      leftDisplay: t101,
      rightDisplay: t104,
      variant: "bar"
    }];
    $[158] = competitionStats.awayTeamScoredFirst;
    $[159] = competitionStats.homeTeamScoredFirst;
    $[160] = labels.homeVsAway;
    $[161] = t101;
    $[162] = t104;
    $[163] = t105;
  } else {
    t105 = $[163];
  }
  let t106;
  if ($[164] === Symbol.for("react.memo_cache_sentinel")) {
    t106 = getH2hStyles();
    $[164] = t106;
  } else {
    t106 = $[164];
  }
  let t107;
  if ($[165] !== t105) {
    t107 = <StatsHeadToHead items={t105} styles={t106} />;
    $[165] = t105;
    $[166] = t107;
  } else {
    t107 = $[166];
  }
  let t108;
  if ($[167] !== t107 || $[168] !== t95) {
    t108 = <div className="md:col-span-2 border border-border rounded-lg bg-card" style={t92}>{t95}{t107}</div>;
    $[167] = t107;
    $[168] = t95;
    $[169] = t108;
  } else {
    t108 = $[169];
  }
  let t109;
  if ($[170] !== competitionStats.bothTeamsScoredPercentage) {
    t109 = competitionStats.bothTeamsScoredPercentage.toFixed(0);
    $[170] = competitionStats.bothTeamsScoredPercentage;
    $[171] = t109;
  } else {
    t109 = $[171];
  }
  let t110;
  if ($[172] !== t109) {
    t110 = <span className="text-4xl font-bold text-foreground">{t109}%</span>;
    $[172] = t109;
    $[173] = t110;
  } else {
    t110 = $[173];
  }
  let t111;
  if ($[174] !== labels.bothTeamsScored) {
    t111 = <span className="text-sm text-muted-foreground uppercase mt-1">{labels.bothTeamsScored}</span>;
    $[174] = labels.bothTeamsScored;
    $[175] = t111;
  } else {
    t111 = $[175];
  }
  let t112;
  if ($[176] !== t110 || $[177] !== t111) {
    t112 = <div className="flex flex-col items-center justify-center bg-card border border-border rounded-lg p-6">{t110}{t111}</div>;
    $[176] = t110;
    $[177] = t111;
    $[178] = t112;
  } else {
    t112 = $[178];
  }
  let t113;
  if ($[179] !== t108 || $[180] !== t112) {
    t113 = <div className="grid grid-cols-1 md:grid-cols-3 gap-1">{t108}{t112}</div>;
    $[179] = t108;
    $[180] = t112;
    $[181] = t113;
  } else {
    t113 = $[181];
  }
  let t114;
  if ($[182] !== t11 || $[183] !== t113 || $[184] !== t31 || $[185] !== t46 || $[186] !== t52 || $[187] !== t70 || $[188] !== t90) {
    t114 = <div className="space-y-8">{t11}{t31}{t46}{t52}{t70}{t90}{t113}</div>;
    $[182] = t11;
    $[183] = t113;
    $[184] = t31;
    $[185] = t46;
    $[186] = t52;
    $[187] = t70;
    $[188] = t90;
    $[189] = t114;
  } else {
    t114 = $[189];
  }
  return t114;
}